import type { ContextUsage } from '@/frontend/features/AgentChats/events/agentEvents'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

function compactTokens(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    notation: value >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value)
}

interface ContextUsageBadgeProps {
  usage: ContextUsage
  className?: string
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
}

export default function ContextUsageBadge({
  usage,
  className,
  side = 'bottom',
  align = 'end',
}: ContextUsageBadgeProps) {
  const window = usage.modelContextWindow
  const percentage = window && window > 0
    ? Math.min(100, Math.round((usage.contextTokens / window) * 100))
    : null
  const progress = percentage ?? 0
  const remaining = window ? Math.max(0, window - usage.contextTokens) : null
  const usedLabel = usage.contextTokens.toLocaleString('pt-BR')
  const windowLabel = window?.toLocaleString('pt-BR')
  const remainingLabel = remaining?.toLocaleString('pt-BR')
  const summary = window
    ? `${compactTokens(usage.contextTokens)} de ${compactTokens(window)} tokens em uso${percentage === null ? '' : ` (${percentage}%)`}`
    : `${compactTokens(usage.contextTokens)} tokens em uso`

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`Uso do contexto: ${summary}`}
          data-testid="agent-context-usage"
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground/65',
            'transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            className,
          )}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            className="size-5 -rotate-90 overflow-visible"
          >
            <circle
              cx="10"
              cy="10"
              r="7.5"
              fill="none"
              pathLength="100"
              stroke="currentColor"
              strokeWidth="2.5"
              className="opacity-20"
            />
            <circle
              cx="10"
              cy="10"
              r="7.5"
              fill="none"
              pathLength="100"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={`${progress} ${100 - progress}`}
              className="opacity-80 transition-[stroke-dasharray] duration-300 motion-reduce:transition-none"
            />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent side={side} align={align} className="w-64 p-3">
        <div className="space-y-2">
          <div>
            <p className="text-xs font-semibold text-popover-foreground">Contexto</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{summary}</p>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] tabular-nums">
            <dt className="text-muted-foreground">Em uso</dt>
            <dd className="text-right text-popover-foreground">{usedLabel}</dd>
            <dt className="text-muted-foreground">Limite</dt>
            <dd className="text-right text-popover-foreground">{windowLabel ?? 'Indisponível'}</dd>
            <dt className="text-muted-foreground">Disponível</dt>
            <dd className="text-right text-popover-foreground">{remainingLabel ?? 'Indisponível'}</dd>
          </dl>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
